import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GlobalProvider } from "@/context/GlobalContext";
import { BottomNav } from "@/components/BottomNav";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Feed from "@/pages/Feed";
import EventDetail from "@/pages/EventDetail";
import PostEvent from "@/pages/PostEvent";
import Dating from "@/pages/Dating";
import Matches from "@/pages/Matches";
import Chats from "@/pages/Chats";
import ChatThread from "@/pages/ChatThread";
import Profile from "@/pages/Profile";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import "@/App.css";

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  return children;
};

const FullScreenSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0B0F17]">
    <div className="w-10 h-10 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const AppShell = () => {
  const location = useLocation();
  // Google OAuth returns to /auth/callback#session_id=...
  // Detect the fragment SYNCHRONOUSLY here (per Emergent Auth playbook)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const hideNav = ["/auth", "/", "/payment/success", "/payment/cancel", "/auth/callback"].some(
    (p) => location.pathname === p
  );
  return (
    <div className="App min-h-screen">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/feed" element={<RequireAuth><Feed /></RequireAuth>} />
        <Route path="/event/:id" element={<RequireAuth><EventDetail /></RequireAuth>} />
        <Route path="/post" element={<RequireAuth><PostEvent /></RequireAuth>} />
        <Route path="/dating" element={<RequireAuth><Dating /></RequireAuth>} />
        <Route path="/matches" element={<RequireAuth><Matches /></RequireAuth>} />
        <Route path="/chats" element={<RequireAuth><Chats /></RequireAuth>} />
        <Route path="/chat/:userId" element={<RequireAuth><ChatThread /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
      {!hideNav && <BottomNav />}
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <GlobalProvider>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </GlobalProvider>
    </BrowserRouter>
  );
}

export default App;
